/**
 * The invitation waiting in this browser until the person joins
 * (invite-onboarding, Decided 8): `{ token, proof, expiresAt }`.
 *
 * Opening the emailed link saves it at once, before any gate runs, so it
 * survives everything between the click and the join: making an account, a
 * vendor's sign-in page, the email code typed in another tab, a reload. It is
 * cleared on a join that landed, on a link the server says is dead, on "Stay
 * signed in as …", and when it expires.
 *
 * In `localStorage`, which may be blocked, full or missing: every read and
 * write is guarded, and a browser that cannot keep it simply has none, which
 * is today's routing.
 */
import {
  inviteProofFromUrl,
  inviteTokenFromUrl,
  pendingInviteLive,
  type PendingInvite,
} from '@alluzo/shared';
import { useSyncExternalStore } from 'react';

const KEY = 'recordos.pending-invite.v1';

/** The held value, or null for anything that is not one. Never throws. */
export const parsePendingInvite = (raw: string | null): PendingInvite | null => {
  if (raw === null || raw === '') return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.token !== 'string' || row.token.trim() === '') return null;
  return {
    token: row.token,
    proof: typeof row.proof === 'string' && row.proof !== '' ? row.proof : null,
    expiresAt: typeof row.expiresAt === 'string' ? row.expiresAt : null,
  };
};

const storage = (): Storage | null => {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

const readRaw = (): string | null => {
  try {
    return storage()?.getItem(KEY) ?? null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------ listeners */

const listeners = new Set<() => void>();
const notify = (): void => {
  for (const listener of listeners) listener();
};

const writeRaw = (raw: string | null): void => {
  try {
    const store = storage();
    if (store !== null) {
      if (raw === null) store.removeItem(KEY);
      else store.setItem(KEY, raw);
    }
  } catch {
    // Kept nowhere: this browser routes as it would without an invitation.
  }
  notify();
};

/* ------------------------------------------------------------ the store */

/** Keeps the invitation, replacing any other. */
export const savePendingInvite = (pending: PendingInvite): void => {
  writeRaw(JSON.stringify({ token: pending.token, proof: pending.proof ?? null, expiresAt: pending.expiresAt }));
};

export const clearPendingInvite = (): void => {
  if (readRaw() === null) return;
  writeRaw(null);
};

/** The live invitation, or null. One that has expired is dropped as it is read. */
export const readPendingInvite = (now: Date = new Date()): PendingInvite | null => {
  const raw = readRaw();
  const pending = parsePendingInvite(raw);
  if (pending !== null && pendingInviteLive(pending, now)) return pending;
  if (raw !== null) writeRaw(null);
  return null;
};

/** The preview answered: its day of expiry is written in, for this token only. */
export const notePendingExpiry = (token: string, expiresAt: string): void => {
  const pending = parsePendingInvite(readRaw());
  if (pending === null || pending.token !== token || pending.expiresAt === expiresAt) return;
  savePendingInvite({ ...pending, expiresAt });
};

/**
 * The `/join?code=…&proof=…` the browser opened, kept at once. The same
 * token already held keeps the expiry the preview wrote while that expiry is
 * still ahead; one that has passed is forgotten, because a resent invitation
 * keeps its token and moves its expiry on, and the preview writes the new
 * one. A proof is never lost to a later copy of the link that does not carry
 * one.
 */
export const captureInviteFromUrl = (href: string, now: Date = new Date()): PendingInvite | null => {
  const token = inviteTokenFromUrl(href);
  if (token === null) return null;
  const proof = inviteProofFromUrl(href);
  const held = parsePendingInvite(readRaw());
  const same = held !== null && held.token === token;
  const next: PendingInvite = {
    token,
    proof: proof ?? (same ? (held.proof ?? null) : null),
    expiresAt: same && pendingInviteLive(held, now) ? held.expiresAt : null,
  };
  savePendingInvite(next);
  return next;
};

/* ------------------------------------------------------------- the hook */

let cachedRaw: string | null = null;
let cachedValue: PendingInvite | null = null;

const snapshot = (): PendingInvite | null => {
  const raw = readRaw();
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedValue = parsePendingInvite(raw);
  }
  return cachedValue;
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  // Another tab of this browser: the email code typed there, a join made there.
  const onStorage = (event: StorageEvent): void => {
    if (event.key === null || event.key === KEY) listener();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
};

/** The live invitation on this browser, re-read whenever it changes here or in another tab. */
export const usePendingInvite = (): PendingInvite | null => {
  const held = useSyncExternalStore(subscribe, snapshot, () => null);
  return held !== null && pendingInviteLive(held, new Date()) ? held : null;
};
